/**
 * RecipePickerDialog
 *
 * Allows the user to pick a recipe and add it as a single line item to the
 * daily intake for a given meal category. The recipe is added with its
 * default finished weight (celkova_hmotnost_g) and pantry items are
 * reserved immediately on the server.
 *
 * Each recipe card shows:
 *  - Recipe name + overall pantry availability badge
 *  - Per-ingredient badges coloured by pantry status:
 *      green  = enough in pantry
 *      yellow = partial (some available, not enough)
 *      red/gray = missing from pantry
 *  - Pantry match progress bar (X/Y in pantry)
 *  - Weight and kcal/100g summary
 */

import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";

interface RecipePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  klientId: number;
  datum: string;
  prijemKategoriaId: number;
  onApplied: () => void;
}

type IngredientStatus = "green" | "yellow" | "red";

export function RecipePickerDialog({
  open,
  onOpenChange,
  klientId,
  datum,
  prijemKategoriaId,
  onApplied,
}: RecipePickerDialogProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const utils = trpc.useUtils();

  const { data: recipes, isLoading } = trpc.recept.list.useQuery(
    { klientId },
    { enabled: open }
  );

  // Pantry availability for ingredient color coding
  const { data: availabilityData } = trpc.spajza.getAvailability.useQuery(
    { klientId },
    { enabled: open }
  );

  // potravinaId → available grams
  const pantryMap = useMemo<Record<number, number>>(() => {
    if (!availabilityData) return {};
    const m: Record<number, number> = {};
    for (const e of availabilityData) m[e.potravinaId] = e.dostupneG;
    return m;
  }, [availabilityData]);

  const applyMutation = trpc.recept.applyRecipe.useMutation({
    onSuccess: () => {
      toast.success(
        t("mealLibrary:recept.applied", "Recept pridaný do príjmu")
      );
      utils.prijem.getByKlientAndDate.invalidate();
      utils.prijem.getDailyTotals.invalidate();
      utils.spajza.getAvailability.invalidate();
      onApplied();
    },
    onError: err => {
      toast.error(`Chyba: ${err.message}`);
    },
  });

  const filtered = useMemo(() => {
    if (!recipes) return [];
    const q = search.trim().toLowerCase();
    if (!q) return recipes;
    return recipes.filter(r => r.nazov.toLowerCase().includes(q));
  }, [recipes, search]);

  /** Per-ingredient pantry status */
  function getIngredientStatus(
    potravinaId: number,
    hmotnostG: string
  ): IngredientStatus {
    const avail = pantryMap[potravinaId] ?? 0;
    const needed = parseFloat(hmotnostG) || 0;
    if (avail <= 0) return "red";
    if (needed > 0 && avail < needed) return "yellow";
    return "green";
  }

  /** Overall recipe pantry status */
  function getRecipeStatus(
    recipe: (typeof filtered)[0]
  ): "full" | "partial" | "missing" {
    if (!recipe.items || recipe.items.length === 0) return "missing";
    let fullCount = 0;
    let partialCount = 0;
    for (const item of recipe.items) {
      if (!item.potravinaId) continue;
      const status = getIngredientStatus(
        item.potravinaId,
        item.hmotnostG ?? "0"
      );
      if (status === "green") fullCount++;
      else if (status === "yellow") partialCount++;
    }
    const total = recipe.items.filter(p => p.potravinaId).length;
    if (total === 0) return "missing";
    if (fullCount === total) return "full";
    if (fullCount + partialCount > 0) return "partial";
    return "missing";
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("mealLibrary:recept.pickTitle", "Vybrať recept")}
          </DialogTitle>
        </DialogHeader>

        {/* Search */}
        <div className="relative shrink-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-8"
            placeholder={t(
              "mealLibrary:recept.searchPlaceholder",
              "Hľadať recept…"
            )}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Recipe list */}
        <ScrollArea className="h-[min(420px,55vh)]">
          <div className="space-y-2 pr-3">
            {isLoading && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                {t("common:loading", "Načítavam…")}
              </div>
            )}
            {!isLoading && filtered.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                {t("mealLibrary:recept.noResults", "Žiadne recepty")}
              </div>
            )}
            {filtered.map(recipe => {
              const status = getRecipeStatus(recipe);
              const validItems = recipe.items?.filter(p => p.potravinaId) ?? [];
              const totalIngredients = validItems.length;
              const inPantry = validItems.filter(
                p =>
                  getIngredientStatus(p.potravinaId!, p.hmotnostG ?? "0") ===
                  "green"
              ).length;
              const pct =
                totalIngredients > 0
                  ? Math.round((inPantry / totalIngredients) * 100)
                  : 0;

              const kcalPer100 = recipe.per100g?.energiaKcal
                ? `${Math.round(parseFloat(recipe.per100g.energiaKcal))} kcal/100g`
                : null;

              return (
                <div
                  key={recipe.id}
                  className="rounded-lg border px-3 py-2.5 hover:bg-muted/40 transition-colors"
                >
                  {/* Header row: name + pantry badge + add button */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                        <span className="truncate">{recipe.nazov}</span>
                        {status === "full" && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-green-50 text-green-700 border-green-300 shrink-0"
                          >
                            ✓ špajza
                          </Badge>
                        )}
                        {status === "partial" && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-yellow-50 text-yellow-700 border-yellow-300 shrink-0"
                          >
                            ~ špajza
                          </Badge>
                        )}
                      </div>

                      {/* Weight / kcal summary */}
                      <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-3">
                        {recipe.effectiveHmotnostG > 0 && (
                          <span>{recipe.effectiveHmotnostG} g</span>
                        )}
                        {kcalPer100 && <span>{kcalPer100}</span>}
                      </div>
                    </div>

                    {/* Pantry match score */}
                    {totalIngredients > 0 && (
                      <div className="shrink-0 w-24 text-right">
                        <span
                          className={[
                            "text-xs font-semibold",
                            pct === 100
                              ? "text-green-600"
                              : pct >= 50
                                ? "text-yellow-600"
                                : "text-muted-foreground",
                          ].join(" ")}
                        >
                          {pct}%
                        </span>
                        <Progress value={pct} className="h-1.5 mt-1" />
                        <span className="text-[10px] text-muted-foreground">
                          {inPantry}/{totalIngredients}{" "}
                          {t("mealLibrary:sablona.inPantry", "v špajzi")}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Per-ingredient badges with pantry colour coding */}
                  {validItems.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {validItems.map(item => {
                        const ingStatus = getIngredientStatus(
                          item.potravinaId!,
                          item.hmotnostG ?? "0"
                        );
                        const cls =
                          ingStatus === "green"
                            ? "border-green-400 bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-300"
                            : ingStatus === "yellow"
                              ? "border-yellow-400 bg-yellow-50 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300"
                              : "border-border text-muted-foreground";
                        const tooltip =
                          ingStatus === "green"
                            ? "Dostupné v špajzi"
                            : ingStatus === "yellow"
                              ? "Čiastočne dostupné"
                              : "Chýba v špajzi";
                        return (
                          <Badge
                            key={item.id}
                            variant="outline"
                            className={`text-xs border ${cls}`}
                            title={tooltip}
                          >
                            {item.potravina.nazov}
                            {item.hmotnostG && (
                              <span className="ml-1 opacity-70">
                                {item.hmotnostG}g
                              </span>
                            )}
                          </Badge>
                        );
                      })}
                    </div>
                  )}

                  {/* Add button */}
                  <div className="flex justify-end mt-2">
                    <Button
                      size="sm"
                      disabled={applyMutation.isPending}
                      onClick={() =>
                        applyMutation.mutate({
                          receptId: recipe.id,
                          klientId,
                          datum,
                          prijemKategoriaId,
                        })
                      }
                    >
                      {t("mealLibrary:recept.add", "Pridať")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
